import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { AuthContext } from "core/contexts/auth/auth";
import { getFilterCount, getFilters } from "core/hooks/filters";
import { useTheme } from "styled-components";
import { getHomeMetrics } from "modules/flywheel/services/incidentService";
import { getAnalystDashCasesGridBoxData, getAnalystDashIncidentTrends } from "modules/flywheel/utils/incidentUtils";
import { 
  setAreUserEventsLoading, 
  setIncidentStatus, 
  setIncidentsByPage, 
  setSelectedRow, 
  setSideModal, 
  setWeeklyIncidents, 
  updateWeeklyIncidents 
} from "modules/flywheel/store/incident";

import Datagrid from "core/components/global/tables/datagrid/dataGrid";
import { ISortData } from "../flywheelIncidents/incidentTable";
import AnalystToolbar from "./analystToolbar";
import { DashboardItem, DashboardColumn } from "core/components/layout/dashboards/twoColumns/dashboard";
import { StyledDashContainer } from "core/components/layout/dashboards/twoColumns/dashboard.styled";
import GridPageLayout from "core/components/layout/grid/gridLayout/gridPageLayout";
import StackedAreaChart from "./charts/stackedAreaChart";
import CustomLegend from "./dashboard/customLegend";
import HorizontalBarChart from "./charts/horizontalBarChart";
import CustomPieChart from "./charts/customPieChart";
import { Box } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import CyberIntelligence from "./cyberIntelligence/CyberIntelligence";
import { useChatbot } from "../flywheelIncidents/searchAgent/chatBotContext";
import { useNavigate } from "react-router";
import IncidentSidePanel from "../flywheelIncidents/incidentSidePanel";
interface activity { 
  description: string; count: number; circleColor?: string; icon?: any; percentage: number;
}

interface urgency { 
  description: string; count: number; circleColor?: string; 
}

interface trendCriticality { 
  color: string; count: number;
}

interface trends { 
  name: string; low: trendCriticality; medium: trendCriticality, high: trendCriticality
}

const gridBoxData = [
  { num: 0, type: "Assigned to me", color: "fff" },
  { num: 0, type: "Unassigned", color: "fff" },
  { num: 0, type: "Critical incidents today", color: "fff" },
];

// Source these from somewhere
const threat_feeds = [
  {"Campaign": "NotPetya", "Date": "2024-02-25"},
  {"Campaign": "Sodinokibi", "Date": "2024-02-10"},
  {"Campaign": "Zeus", "Date": "2024-02-14"},
  {"Campaign": "TrickBot", "Date": "2024-01-08"},
  {"Campaign": "Emotet", "Date": "2023-12-22"},
  {"Campaign": "DarkTequila", "Date": "2023-11-18"},
  {"Campaign": "WannaCry", "Date": "2023-10-30"},
  {"Campaign": "Ryuk", "Date": "2023-10-15"},
  {"Campaign": "Maze", "Date": "2023-12-20"},
  {"Campaign": "LockBit", "Date": "2023-11-05"}
]

const reports = [{
  "name": "Oracle Quarterly Critical Patch Update: January 2024"
}, {
  "name": "Cisco Releases Multiple Advisories to Fix 32 Vulnerabilities Affecting Multiple Products"
}, {
  "name": "Google Releases New Version of Android to Address 78 Vulnerabilities in Google Android"
}, {
  "name": "Microsoft Security Bulletin Review for November 2023"
}, {
  "name": "Google Releases New Version of Chrome (119.0.6045.199) for Mac and Linux (119.0.6045.199/.200) and for Windows to Fix 6 Vulnerabilities"
}, {
  "name": "Google Releases New Version of Chrome (120.0.6099.62) for Mac and Linux (120.0.6099.62/.63) and for Windows to Fix 5 Vulnerabilities"
}, {
  "name": "Iran and Russia Poised for More Cyber Cooperation"
}, {
  "name": "Cyber Threats to ICS Environments from 2022 to Early 2023"
}]


interface Params {
    target: string;
    aFAccessToken: string;
    page: number;
    size: number;
    orderBy: string;
    order: string;
    filters: object;
    search: string;
}

/**
 * This component tries to separate out the related components
 * that do not have to do with the Redux list of alerts and 
 * those that do, this is so that only the affected components
 * are refreshed when data is added or changed.
 * @returns 
 */
export const AnalystTable = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const alertState = useAppSelector((state) => state.alerts)
    
    const status_color_mapping = {
      "Open": theme.colors.open_incident,
      "In Progress": theme.colors.in_progress_incident,
      "Closed": theme.colors.closed_incident,
      "Request Tuning": theme.colors.requested_incident,
    }
    const [page, setPage] = useState(0);
    const [gridBox, setGridBox] = useState(gridBoxData);
    const [casesByUrgency, setCasesByUrgency] = useState<urgency[]>([]);
    const [weekServiceActivity, setWeekServiceActivity] = useState<activity[]>([]);
    const [allServiceActivity, setAllServiceActivity] = useState<activity[]>(
      [{"description":"Open","count":1,"circleColor":status_color_mapping["Open"],"percentage":0},{"description":"In Progress","count":0,"circleColor":status_color_mapping["In Progress"],"percentage":0},{"description":"Closed","count":0,"circleColor":status_color_mapping["Closed"],"percentage":0},{"description":"Request Tuning","count":0,"circleColor":status_color_mapping["Request Tuning"],"percentage":0}]
    );
    const [incidentTrends, setIncidentTrends] = useState<trends[]>([]);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const { amsUser: user } = useContext(AuthContext);
    const incidentState = useAppSelector((state) => state.incidents);
    const dispatch = useAppDispatch();
    const { checkAuthToken } = useContext(AuthContext)
    const [pageSize, setPageSize] = useState(5);
    const [sortData, setSortData] = useState<ISortData>({
      field: "findingId",
      order: "desc",
    });
  
    const statusMapping = {
        "Open": theme.colors.open_incident,
        "In Progress": theme.colors.in_progress_incident,
        "Closed": theme.colors.closed_incident,
        "Request Tuning": theme.colors.requested_incident
    }

    /**
     * Handle the expanded incident modal
     * @param id 
     */
    const onIncidentClick = async (id, type) => {  
        if (id) {
            dispatch(setSelectedRow({id: id, type: type}));
            dispatch(setSideModal(true));
        }
    };

    /**
     * Handle filter data from the table
     */
    const userCaseState = useAppSelector((state) => state.incidents);
    const [filterData, setFilterData] = useState({ "totalFilters": 0, "filterTerms": {}, "searchTerm": ""});

    async function handleFilterData({
        filter, 
        argPageSize = pageSize, 
        argPageNum = page,
        argSortField = sortData.field, 
        argSortOrder = sortData.order
      }) {
      const token = await checkAuthToken();  
      const params: Params = {
        target: "home",
        aFAccessToken: token, 
        page: argPageNum,
        size: argPageSize,
        orderBy: argSortField,
        order: argSortOrder,
        filters: filterData["filterTerms"],
        search: filterData["searchTerm"]
      }

      // A new filter has been provided, parse an add replace the existing
      params.filters = getFilters(filter, params);

      // As this is the user home only make the request if the user can be found
      if (user) {
        // Add the user's email as a filter
        params.filters["filter[assignedto]"] = user.email;

        // Send the request to the backend
        dispatch(setIncidentsByPage(params));
        
        // Save the current filter for the next request
        setFilterData({
          totalFilters: getFilterCount(filterData["searchTerm"], params.filters),
          filterTerms: params.filters,
          searchTerm: filterData["searchTerm"]
        })
      } else {
        // TODO: implement logout user
        console.error("Cannot find user")
      }
    }

    async function onSortChange(field: string, order: string) {
        setSortData({ field, order });
        handleFilterData({
          filter: {}, 
          argSortOrder: order, 
          argSortField: field === "id" ? "findingId" : field
        });
    }
    
    async function onPageChange(pageNum) {
        handleFilterData({filter: {}, argPageNum: pageNum});
    }
    
    async function handleRowsPerPageChange(pageSize) {
        setPageSize(pageSize)
        handleFilterData({filter: {}, argPageSize: pageSize});
    }

    /**
     * Set the status of an incident
     * @param status 
     * @param id 
     */
    const handleSetStatus = async (status: string, id: string) => {
        if (status) {
            const token = await checkAuthToken();
            dispatch(setIncidentStatus({'aFAccessToken': token, 'status': status, 'id': id}));
        }
    }
  
    async function deleteFilter(key, value) {
      const token = await checkAuthToken();
  
      const tempTerms: { [key: string]: any } = filterData["filterTerms"]
      const filterValues = String(tempTerms[key]);
  
      if (filterValues) {
        if (filterValues.includes(",")) {
          // When filters contain multiple values per Filter Type
          tempTerms[key] = filterValues.split(',').filter(item => item !== value).join(',');
        } else {
          tempTerms[key] = ""
        }
  
        for (const key in tempTerms) {
          if (tempTerms[key] === "") {
            delete tempTerms[key];
          }
        }
  
        setFilterData(prev => ({
          filterTerms: tempTerms,
          totalFilters: getFilterCount(filterData["searchTerm"], tempTerms),
          searchTerm: filterData["searchTerm"]
        }));
  
        dispatch(
          setIncidentsByPage(
            {
              target: "home",
              aFAccessToken: token, 
              page: page,
              size: pageSize,
              orderBy: sortData.field,
              order: sortData.order,
              filters: tempTerms ? tempTerms : filterData["filterTerms"],
              search: filterData["searchTerm"]
            }
          )
        );
      }
    }

    /**
     * Fetch the user's associated incidents
     */
    const fetchUserIncidentsByPage = async () => {
        const token = await checkAuthToken();        
        dispatch(
            setIncidentsByPage(
            {
              target: "home",
              aFAccessToken: token, 
              page: page,
              size: pageSize,
              orderBy: sortData.field,
              order: sortData.order,
              filters: {...filterData["filterTerms"], ...{"filter[assignedto]": user?.email}},
              search: filterData["searchTerm"]
            }
          )
        );
    };

    /**
     * Filter by week and day from both the user and all pageEvents to 
     * populate thw statistics gridbar at the top of the Component
     * @param pageEvents 
     */
    async function setGridData(eventsFromLast7Days) {
        const token = await checkAuthToken();
        const config = { 'Authorization': token || '', 'Content-Type': 'application/json' };
        // Assuming 'pageEvents' is your array of pageEvents
        let timeNow: number = Math.round(new Date().getTime() / 1000);
        
        const homeMetrics = await getHomeMetrics(config);
        if (homeMetrics) {
          // Iterate 7 day data 
          const result = getAnalystDashCasesGridBoxData(t, theme, eventsFromLast7Days, homeMetrics["totalResults"]);
          setCasesByUrgency(result.urgencyValues);
          setWeekServiceActivity(result.activityValues);

          // Incident Prioritization Status - GET STATUS Metrics
          if (homeMetrics["status"]) {
            const incidentPrioritizationActivity = Object.entries(homeMetrics["status"]).map(([status, count]: [string, number]) => {
              return {description: status, count: count, circleColor: status_color_mapping[status], percentage: parseFloat(((count/homeMetrics["totalRecords"]) * 100).toFixed(2))}
            })
            setAllServiceActivity(incidentPrioritizationActivity);
          }
        }

        // Get the StackedChart Data, Incidents past 7 days severity (H, M, L)
        const result2 = getAnalystDashIncidentTrends(eventsFromLast7Days);
        setIncidentTrends(result2 as trends[]);  

        // Check if the event is critical and the event's lastUpdated date is within the last day
        const criticalEvents = eventsFromLast7Days.filter(event => {
          const timeThen: number = new Date(event.createdDate as string).getTime();
          if (timeNow.toString().length !== timeThen.toString().length) {
              timeNow = timeNow * 1000;
          }
          return event.severity === "Critical" && timeNow - timeThen <= 1 * 24 * 60 * 60 * 1000;
        });

        // Set the data for the top bar
        const tempData = gridBox.map((item) => {
          if (item.type === "Assigned to me") {
              item.num = userCaseState.userEvents.length || 0;
          } else if (item.type === "Unassigned") {
              item.num = homeMetrics && homeMetrics["unassigned"] ? homeMetrics["unassigned"] : 0
          } else if (item.type === "Critical incidents today") {
              item.num = criticalEvents.length || 0;
          }
          return item;
        });
        
        setGridBox(tempData);
    }

    useEffect(() => {
      dispatch(setAreUserEventsLoading(true));
      
      // If not currently fetching and has already instanciated the first call then fetch on the interval
      const fetchData = async () => {
        // Fetch user data
        fetchUserIncidentsByPage();

        // Fetch metrics data
        const token = await checkAuthToken();
        await dispatch(updateWeeklyIncidents(token));
      };
      
      // Set the interval, every X seconds 'fetchData()' will be called
      // const intervalId = setInterval(fetchData, 30000);
      
      // // Once the component closes, close this interval: only fetch when in focus
      // return () => {
      //     clearInterval(intervalId);
      // };
    }, [page, pageSize, sortData, filterData]);
 
    useEffect(() => {
      // On every completed data fetch, follow with update the metrics
      if (!userCaseState.isFetching) {
        setGridData(userCaseState.weeklyEvents)
      }
    }, [userCaseState.isFetching])

    

    async function fetchWeekData() {
      const token = await checkAuthToken();
      dispatch(setWeeklyIncidents(token))
    }
    
    useEffect(() => {
      if(user !== null) {
        // Fetch user data
        fetchUserIncidentsByPage();
        // Fetch data for the week
        fetchWeekData();
      }
    }, [user]);
    const { showChatbot } = useChatbot();
    const navigate = useNavigate();
     const onActionClick = async (rowId: string, type: string) => {
        console.log("onActionClick IncidentTable component ", rowId, type);
        dispatch(setSelectedRow({ id: rowId, type: type }));
    
        if (type === "searchAgentSuite") {
          console.log(`1`);
          showChatbot();
    
        } else if (type === "searchSimilar") {
          console.log(`2`);
          navigate(`/console/cr/flywheel/incidents/search`);
    
        } else if (type === "viewIncident") {
          console.log(`3`);
          dispatch(setSideModal(true));
        } else {
          console.log(`4`);
          console.log(`Unknown action type: ${type}`);
        }
        console.log("5");
      };
    
    return(
      <>
     
        <div
          className={`${incidentState.previewWidth === 100 ? "" : "ps-3 pb-3 pe-3"} flex-grow flex-shrink-1 overflow-scroll`}
          style={{
            flexGrow: 0,
            flexShrink: 1,
            width: `${
              incidentState.previewWidth
                ? 100 - incidentState.previewWidth
                : 100
            }%`,
            transition: "width 0.2s ease-in-out",
          }}
        >
     
        <GridPageLayout {...gridBox} />
        <div className="analystDash-cases-toolbar">
            <AnalystToolbar
              headCells={userCaseState.userHeaders}
              setFilterData={handleFilterData}
              isFilterModalOpen={isFilterModalOpen}
              setPage={setPage}
              setIsFilterModalOpen={setIsFilterModalOpen} 
              filterData={filterData}
              deleteFilter={deleteFilter}
            />
        </div>
      
        
          <DashboardColumn>
            <DashboardItem title={t("ANALYST_DASH.ASSIGNED_TO_ME")}>
                <Datagrid
                    rowData={userCaseState.userEvents}
                    headCells={userCaseState.userHeaders}
                    visibleKeys={userCaseState.userHeaders
                      .filter((header) => header.showColumnUnExpanded)
                      .map((header) => header.id)}
                    maxWidths={{
                        entity: 200,
                        classification: 200,
                    }}
                    showSelectionCheckbox={false}
                    showPagination={true}
                    pageSize={pageSize}
                    totalData={userCaseState.totalRowCount}
                    isFilterModalOpen={isFilterModalOpen}
                    setIsFilterModalOpen={setIsFilterModalOpen}
                    filterData={filterData}
                    onRowClick={onIncidentClick}
                    highlightedRowId={userCaseState.selectedRow}
                    setFilterData={handleFilterData}
                    statusMapping={statusMapping}
                    handleSetStatus={handleSetStatus}
                    isPageLoading={userCaseState.areUserEventsLoading}
                    setPage={setPage}
                    page={page}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onPageChange={onPageChange}
                    onSortChange={onSortChange}
                    onActionClick={onActionClick}
                />
            </DashboardItem>
            
            <DashboardItem title="Incident Trends (last 7 days)">
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                  <StackedAreaChart 
                      data={incidentTrends}
                      theme={theme}
                  />
                  <div className="align-self-baseline">
                  <CustomLegend
                      flexDirection="row"
                      data={[
                      {
                          "text": "Fatal",
                          "color": theme.colors.fatal_criticality
                      },
                      {
                          "text": "Critical",
                          "color": theme.colors.critical_criticality
                      },
                      {
                          "text": "High",
                          "color": theme.colors.high_criticality
                      },
                      {
                          "text": "Medium",
                          "color": theme.colors.medium_criticality
                      },
                      {
                          "text": "Low",
                          "color": theme.colors.low_criticality
                      },
                      {
                          "text": "Informative",
                          "color": theme.colors.informative_criticality
                      },
                      {
                          "text": "Unclassified",
                          "color": theme.colors.unclassified_criticality
                      }
                      ]} 
                  />
                  </div>
              </Box>
            </DashboardItem>

            <DashboardItem title="Cases by urgency (last 7 days)">
              <Box className="m-0 d-flex justify-content-center flex-column">
                  <HorizontalBarChart 
                  theme={theme}
                  data={casesByUrgency.map((category) => ({
                      color: category.circleColor,
                      value: category.count,
                      name: category.description
                  }))}
                  />
              </Box>
            </DashboardItem>
          </DashboardColumn>

          <DashboardColumn>
            <DashboardItem title="Incident Prioritization Status">
                <Box>
                  <Box className="d-flex align-items-center flex-wrap">
                      <CustomPieChart 
                        isRing={true}
                        text="Overall Health" 
                        data={allServiceActivity.map((category) => ({
                            color: category.circleColor,
                            name: category.description,
                            value: category.count
                        }))} 
                      />
                      <CustomLegend
                        flexDirection="column"
                        data={allServiceActivity.map((category) => ({
                            color: category.circleColor,
                            text: category.description,
                            percentage: category.percentage
                        }))}
                      />
                  </Box>
                </Box>
            </DashboardItem>

            <DashboardItem title="Service Activity (last 7 Days)">
                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                {/* Render CustomPieChart for Open */}
                <CustomPieChart
                    isRing={true}
                    text="Open"
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                      color: category.circleColor,
                      name: category.description,
                      value: category.count
                    }))}
                />

                {/* Render CustomPieChart for In Progress */}
                <CustomPieChart
                    isRing={true}
                    text="In Progress"
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                      color: category.circleColor,
                      name: category.description,
                      value: category.count
                    }))}
                />

                {/* Render CustomPieChart for Closed */}
                <CustomPieChart
                    isRing={true}
                    text="Closed"
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                    color: category.circleColor,
                    name: category.description,
                    value: category.count
                    }))}
                />

                {/* Render CustomPieChart for Closed */}
                <CustomPieChart
                    isRing={true}
                    text="Request Tuning"
                    hasNumber={true}
                    data={weekServiceActivity.map((category) => ({
                    color: category.circleColor,
                    name: category.description,
                    value: category.count
                    }))}
                />
                </Box>

                <Box className="d-flex flex-row flex-wrap align-items-star pt-4">
                {weekServiceActivity.map((category, index) => (
                    <div className="d-flex align-items-center" key={index}>              
                    <CircleIcon
                        style={{
                            color: category.circleColor,
                            verticalAlign: "middle",
                            fontSize: 12,
                            marginRight: 5,
                            marginLeft: 12
                        }}
                    />
                    <div>{category.description}</div>
                    </div>
                ))}
                </Box>
            </DashboardItem>

            <DashboardItem title="Cyber Intelligence Feed">
                <CyberIntelligence threat_feeds={threat_feeds} reports={reports} theme={theme}/>
            </DashboardItem>

          </DashboardColumn>
          
        
      </div>
      
      
      </>
    )
}